import React, { useState, useEffect } from 'react';
import Card from '../components/ui/Card';
import KPICard from '../components/ui/KPICard';
import LineChart from '../components/charts/LineChart';
import BarChart from '../components/charts/BarChart';
import DashboardGrid from '../components/layout/DashboardGrid';
import DashboardGridItem from '../components/layout/DashboardGridItem';
import { CheckCircle, AlertCircle, Activity, Cpu, Database, Globe, Smartphone, Zap } from 'lucide-react';

interface PerformanceMetric {
  label: string;
  value: string | number;
  unit?: string;
  status: 'success' | 'warning' | 'error';
  icon: React.ReactNode;
}

const TechnicalShowcaseDashboard: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [apiCalls, setApiCalls] = useState(0);
  
  // Simulate real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
      setApiCalls(prev => prev + Math.floor(Math.random() * 5) + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Performance metrics
  const performanceMetrics: PerformanceMetric[] = [
    {
      label: 'زمان بارگذاری',
      value: '2.3',
      unit: 'ثانیه',
      status: 'success',
      icon: <Zap className="w-5 h-5" />
    },
    {
      label: 'Uptime',
      value: '99.9',
      unit: '%',
      status: 'success',
      icon: <Activity className="w-5 h-5" />
    }
  ];

  // System architecture components
  const architectureComponents = [
    { name: 'React', category: 'Frontend', version: '19.1.0', status: 'active' },
    { name: 'TypeScript', category: 'Frontend', version: '5.8.3', status: 'active' },
    { name: 'FastAPI', category: 'Backend', version: '0.101.0', status: 'active' },
    { name: 'SQL Server', category: 'Database', version: 'Latest', status: 'active' },
    { name: 'Uvicorn', category: 'Server', version: '0.23.2', status: 'active' },
    { name: 'PyODBC', category: 'Database', version: '4.0.39', status: 'active' }
  ];

  // Real-time monitoring data
  const generateMonitoringData = () => {
    const labels = Array.from({ length: 10 }, (_, i) => `${i * 6}s ago`).reverse();
    return {
      labels,
      datasets: [
        {
          label: 'CPU Usage (%)',
          data: Array.from({ length: 10 }, () => Math.floor(Math.random() * 30) + 20),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4
        },
        {
          label: 'Memory Usage (%)',
          data: Array.from({ length: 10 }, () => Math.floor(Math.random() * 20) + 40),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4
        }
      ]
    };
  };

  // API response times
  const apiResponseData = {
    labels: ['Auth', 'Dashboard', 'Analytics', 'Reports', 'Export', 'Search'],
    datasets: [{
      label: 'Response Time (ms)',
      data: [45, 123, 89, 156, 234, 67],
      backgroundColor: [
        'rgba(59, 130, 246, 0.8)',
        'rgba(16, 185, 129, 0.8)',
        'rgba(251, 191, 36, 0.8)',
        'rgba(239, 68, 68, 0.8)',
        'rgba(168, 85, 247, 0.8)',
        'rgba(245, 158, 11, 0.8)'
      ]
    }]
  };

  return (
    <div className="dashboard-container technical-showcase">
      <div className="dashboard-header">
        <h1 className="dashboard-title">نمایش فنی پلتفرم Smart Insight</h1>
        <p className="dashboard-subtitle">معماری، عملکرد و قابلیت‌های فنی</p>
      </div>

      {/* Performance KPIs */}
      <DashboardGrid columns={{ default: 1, sm: 2 }} className="mb-6">
        {performanceMetrics.map((metric, index) => (
          <DashboardGridItem key={index}>
            <KPICard
              title={metric.label}
              value={metric.value}
              subtitle={metric.unit}
              icon={metric.icon}
              variant={metric.status === 'success' ? 'success' : 'warning'}
            />
          </DashboardGridItem>
        ))}
      </DashboardGrid>

      {/* Main Content Grid */}
      <DashboardGrid columns={{ default: 1, md: 2 }}>
        {/* Frontend Architecture */}
        <DashboardGridItem>
          <Card title="معماری Frontend" className="h-full">
            <div dir="ltr">
            <div className="folder-structure">
              <div className="folder">
                <span className="folder-icon">📁</span> src/
                <div className="sub-items">
                  {/* Folders first */}
                  <div className="folder">
                    <span className="folder-icon">📁</span> components/
                    <div className="sub-items">
                      <div className="folder"><span className="folder-icon">📁</span> auth/ (ProtectedRoute, MagicTokenHandler)</div>
                      <div className="folder"><span className="folder-icon">📁</span> charts/ (LineChart, BarChart, MixedChart)</div>
                      <div className="folder"><span className="folder-icon">📁</span> layout/ (DashboardGrid, DashboardLayout)</div>
                      <div className="folder"><span className="folder-icon">📁</span> archive/mobile/ (legacy mobile dashboard — archived)</div>
                      <div className="folder"><span className="folder-icon">📁</span> ui/ (Card, KPICard, DataTable...)</div>
                    </div>
                  </div>
                  <div className="folder">
                    <span className="folder-icon">📁</span> contexts/ (Auth, Theme, Branch, TimeFrame, Favorites)
                  </div>
                  <div className="folder">
                    <span className="folder-icon">📁</span> hooks/ (useQueries, useBranches, useFavorites)
                  </div>
                  <div className="folder">
                    <span className="folder-icon">📁</span> services/ (apiService, authService)
                  </div>
                  <div className="folder">
                    <span className="folder-icon">📁</span> styles/ (themes, animations, rtl, typography)
                  </div>
                  <div className="folder">
                    <span className="folder-icon">📁</span> types/ (TypeScript definitions)
                  </div>
                  <div className="folder">
                    <span className="folder-icon">📁</span> utils/ (formatters, helpers, apiClient)
                  </div>
                  
                  {/* Then files/dashboards */}
                  <div className="file"><span className="file-icon">📄</span> App.tsx</div>
                  <div className="file"><span className="file-icon">📄</span> index.tsx</div>
                  <div className="file"><span className="file-icon">📄</span> MainPerformanceDashboard.tsx</div>
                  <div className="file"><span className="file-icon">📄</span> ExecutiveDashboard.tsx</div>
                  <div className="file"><span className="file-icon">📄</span> CustomerAnalyticsDashboard.tsx</div>
                  <div className="file"><span className="file-icon">📄</span> SatisfactionDashboard.tsx</div>
                  <div className="more">...و 6 داشبورد دیگر</div>
                </div>
              </div>
            </div>
            </div>
          </Card>
        </DashboardGridItem>

        {/* Backend Architecture */}
        <DashboardGridItem>
          <Card title="معماری Backend" className="h-full">
            <div dir="ltr">
            <div className="folder-structure">
              <div className="folder">
                <span className="folder-icon">📁</span> backend/
                <div className="sub-items">
                  {/* Folders first */}
                  <div className="folder">
                    <span className="folder-icon">📁</span> database/
                    <div className="sub-items">
                      <div className="file"><span className="file-icon">💾</span> SmartPowerBI.sql</div>
                      <div className="file"><span className="file-icon">💾</span> Stored Procedures</div>
                    </div>
                  </div>
                  <div className="folder">
                    <span className="folder-icon">📁</span> routes/
                    <div className="sub-items">
                      <div className="file"><span className="file-icon">🐍</span> customer_analytics_routes.py</div>
                      <div className="file"><span className="file-icon">🐍</span> evaluation_routes.py</div>
                      <div className="file"><span className="file-icon">🐍</span> executive_routes.py</div>
                      <div className="file"><span className="file-icon">🐍</span> hr_routes.py</div>
                      <div className="file"><span className="file-icon">🐍</span> main_performance_routes.py</div>
                      <div className="file"><span className="file-icon">🐍</span> satisfaction_routes.py</div>
                      <div className="more">...و routes دیگر</div>
                    </div>
                  </div>
                  
                  {/* Then files */}
                  <div className="file"><span className="file-icon">🐍</span> auth.py (Authentication logic)</div>
                  <div className="file"><span className="file-icon">🐍</span> auth_routes.py (Auth endpoints)</div>
                  <div className="file"><span className="file-icon">🐍</span> club_routes.py (Club endpoints)</div>
                  <div className="file"><span className="file-icon">🐍</span> config.py (Configuration)</div>
                  <div className="file"><span className="file-icon">🐍</span> data_service.py (Database layer)</div>
                  <div className="file"><span className="file-icon">🐍</span> db_utils.py (Database utilities)</div>
                  <div className="file"><span className="file-icon">🐍</span> main_production.py (FastAPI app)</div>
                  <div className="file"><span className="file-icon">🐍</span> response_utils.py (Response formatting)</div>
                </div>
              </div>
            </div>
            </div>
          </Card>
        </DashboardGridItem>

        {/* Tech Stack Overview */}
        <DashboardGridItem className="md:col-span-2">
          <Card title="Tech Stack کامل پروژه" className="h-full">
            <div className="tech-stack-grid">
              <div className="tech-category">
                <h4 className="category-title">Frontend Core</h4>
                <div className="tech-items">
                  <span className="tech-badge react">React 19.1.0</span>
                  <span className="tech-badge typescript">TypeScript 5.8.3</span>
                  <span className="tech-badge">React Scripts 5.0.1</span>
                  <span className="tech-badge">JavaScript ES6+</span>
                  <span className="tech-badge">HTML5</span>
                </div>
              </div>
              
              <div className="tech-category">
                <h4 className="category-title">UI & Styling</h4>
                <div className="tech-items">
                  <span className="tech-badge tailwind">TailwindCSS 3.4.17</span>
                  <span className="tech-badge">PostCSS 8.5.3</span>
                  <span className="tech-badge">Autoprefixer 10.4.21</span>
                  <span className="tech-badge">Headless UI 2.2.3</span>
                  <span className="tech-badge">RTL Support</span>
                </div>
              </div>
              
              <div className="tech-category">
                <h4 className="category-title">State & Data</h4>
                <div className="tech-items">
                  <span className="tech-badge">TanStack Query 5.76.1</span>
                  <span className="tech-badge">React Query 3.39.3</span>
                  <span className="tech-badge">Axios 1.9.0</span>
                  <span className="tech-badge">React Context API</span>
                  <span className="tech-badge">Local Storage API</span>
                </div>
              </div>
              
              <div className="tech-category">
                <h4 className="category-title">Visualization</h4>
                <div className="tech-items">
                  <span className="tech-badge">Chart.js 4.4.9</span>
                  <span className="tech-badge">React-Chartjs-2 5.3.0</span>
                  <span className="tech-badge">Lucide React 0.525.0</span>
                  <span className="tech-badge">React Icons 5.5.0</span>
                </div>
              </div>
              
              <div className="tech-category">
                <h4 className="category-title">Backend Core</h4>
                <div className="tech-items">
                  <span className="tech-badge python">Python 3.8+</span>
                  <span className="tech-badge fastapi">FastAPI 0.101.0</span>
                  <span className="tech-badge">Uvicorn 0.23.2</span>
                  <span className="tech-badge">Pydantic 2.2.1</span>
                  <span className="tech-badge">PyODBC 4.0.39</span>
                </div>
              </div>
              
              <div className="tech-category">
                <h4 className="category-title">Database & Security</h4>
                <div className="tech-items">
                  <span className="tech-badge sql">SQL Server</span>
                  <span className="tech-badge">Stored Procedures</span>
                  <span className="tech-badge">Python-JOSE 3.3.0</span>
                  <span className="tech-badge">Passlib[bcrypt] 1.7.4</span>
                  <span className="tech-badge">JWT & Magic Link Auth</span>
                </div>
              </div>
              
              <div className="tech-category">
                <h4 className="category-title">Development Tools</h4>
                <div className="tech-items">
                  <span className="tech-badge">React Router 6.30.1</span>
                  <span className="tech-badge">React Select 5.10.1</span>
                  <span className="tech-badge">React Grid Layout 1.5.2</span>
                  <span className="tech-badge">Testing Library</span>
                  <span className="tech-badge">Web Vitals 2.1.4</span>
                </div>
              </div>
              
              <div className="tech-category">
                <h4 className="category-title">Backend Tools</h4>
                <div className="tech-items">
                  <span className="tech-badge">Gunicorn 21.2.0</span>
                  <span className="tech-badge">Python-Multipart</span>
                  <span className="tech-badge">HTTPX 0.24.1</span>
                  <span className="tech-badge">JDatetime 4.1.1</span>
                  <span className="tech-badge">Python-dotenv</span>
                </div>
              </div>
              
              <div className="tech-category">
                <h4 className="category-title">Features</h4>
                <div className="tech-items">
                  <span className="tech-badge">Progressive Web App</span>
                  <span className="tech-badge">Mobile Responsive</span>
                  <span className="tech-badge">Multi-Theme Support</span>
                  <span className="tech-badge">Persian Calendar (JDate)</span>
                  <span className="tech-badge">Real-time Analytics</span>
                </div>
              </div>
            </div>
          </Card>
        </DashboardGridItem>

        {/* API Performance */}
        <DashboardGridItem>
          <Card title="زمان پاسخ API ها" className="h-full">
            <BarChart
              data={apiResponseData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(tickValue: string | number) {
                        return `${tickValue}ms`;
                      }
                    }
                  }
                }
              }}
              height={250}
            />
          </Card>
        </DashboardGridItem>

        {/* System Components Status */}
        <DashboardGridItem>
          <Card title="وضعیت کامپوننت‌های سیستم" className="h-full">
            <div className="space-y-3">
              {architectureComponents.map((component, index) => (
                <div key={index} className="component-status-item">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {component.status === 'active' ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-yellow-500" />
                      )}
                      <div>
                        <span className="font-medium">{component.name}</span>
                        <span className="text-sm text-gray-500 ml-2">v{component.version}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded">
                        {component.category}
                      </span>
                      <span className={`status-indicator ${component.status}`}></span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </DashboardGridItem>
      </DashboardGrid>

      {/* Features Grid */}
      <div className="mt-6">
        <Card title="قابلیت‌های کلیدی پلتفرم">
          <DashboardGrid columns={{ default: 1, md: 3 }} className="gap-4">
            <div className="feature-card">
              <Database className="w-8 h-8 text-blue-500 mb-3" />
              <h4 className="font-semibold mb-2">Real-time Analytics</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                به‌روزرسانی خودکار داده‌ها بدون نیاز به refresh
              </p>
            </div>
            
            <div className="feature-card">
              <Smartphone className="w-8 h-8 text-green-500 mb-3" />
              <h4 className="font-semibold mb-2">Progressive Web App</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                نصب روی موبایل و دسترسی آفلاین
              </p>
            </div>
            
            <div className="feature-card">
              <Globe className="w-8 h-8 text-purple-500 mb-3" />
              <h4 className="font-semibold mb-2">Multi-language Support</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                پشتیبانی از RTL و چند زبانه
              </p>
            </div>
          </DashboardGrid>
        </Card>
      </div>

      {/* Live Clock */}
      <div className="fixed bottom-4 right-4 bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-green-500" />
          <span className="text-sm font-mono">
            {currentTime.toLocaleTimeString('fa-IR')}
          </span>
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .technical-showcase {
          padding: 2rem;
        }

        .dashboard-header {
          margin-bottom: 2rem;
        }

        .dashboard-title {
          font-size: 1.875rem;
          font-weight: bold;
          margin-bottom: 0.5rem;
        }

        .dashboard-subtitle {
          color: var(--color-text-secondary);
        }

        .tech-stack-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1.5rem;
        }

        .tech-category {
          background: #f3f4f6;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1rem;
        }
        
        .dark .tech-category {
          background: #1f2937;
          border: 1px solid #374151;
        }

        .category-title {
          font-size: 0.875rem;
          font-weight: 600;
          color: var(--color-text-secondary);
          margin-bottom: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .folder-structure {
          font-family: monospace;
          font-size: 0.875rem;
        }

        .folder, .file {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.25rem 0;
        }

        .folder-icon, .file-icon {
          font-size: 1rem;
        }

        .sub-items {
          margin-left: 1.5rem;
        }

        .more {
          color: var(--color-text-secondary);
          font-style: italic;
          padding: 0.25rem 0;
        }

        .tech-stack-section {
          margin-top: 1.5rem;
          padding-top: 1rem;
          border-top: 1px solid var(--color-border);
        }

        .tech-title {
          font-size: 0.875rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          color: var(--color-text-secondary);
        }

        .tech-items {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .tech-badge {
          padding: 0.25rem 0.75rem;
          border-radius: 1rem;
          font-size: 0.75rem;
          font-weight: 500;
          background: #6366f1;
          color: white;
          display: inline-block;
          margin: 0.25rem;
          border: 1px solid transparent;
          transition: all 0.2s;
        }
        
        .tech-badge:hover {
          transform: scale(1.05);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .tech-badge.react {
          background: #61dafb;
          color: #282c34;
        }
        
        .tech-badge.typescript {
          background: #3178c6;
          color: white;
        }
        
        .tech-badge.tailwind {
          background: #38b2ac;
          color: white;
        }
        
        .tech-badge.python {
          background: #3776ab;
          color: #ffd43b;
        }
        
        .tech-badge.fastapi {
          background: #009688;
          color: white;
        }
        
        .tech-badge.sql {
          background: #e38c00;
          color: white;
        }
        
        /* Additional badge colors for better visibility */
        .tech-badge:nth-child(4n+1) {
          background: #8b5cf6;
        }
        
        .tech-badge:nth-child(4n+2) {
          background: #10b981;
        }
        
        .tech-badge:nth-child(4n+3) {
          background: #f59e0b;
        }
        
        .tech-badge:nth-child(4n) {
          background: #ef4444;
        }
        
        /* Ensure special badges keep their colors */
        .tech-badge.react,
        .tech-badge.typescript,
        .tech-badge.tailwind,
        .tech-badge.python,
        .tech-badge.fastapi,
        .tech-badge.sql {
          background: var(--bg) !important;
        }
        
        .tech-badge.react { --bg: #61dafb; }
        .tech-badge.typescript { --bg: #3178c6; }
        .tech-badge.tailwind { --bg: #38b2ac; }
        .tech-badge.python { --bg: #3776ab; }
        .tech-badge.fastapi { --bg: #009688; }
        .tech-badge.sql { --bg: #e38c00; }

        .component-status-item {
          padding: 0.75rem;
          border-radius: 0.5rem;
          background: var(--color-surface-secondary);
          transition: all 0.2s;
        }

        .component-status-item:hover {
          transform: translateX(-4px);
        }

        .status-indicator {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          display: inline-block;
        }

        .status-indicator.active {
          background: #10b981;
          box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.2);
        }

        .feature-card {
          padding: 1.5rem;
          border-radius: 0.5rem;
          background: var(--color-surface-secondary);
          text-align: center;
          transition: transform 0.2s;
        }

        .feature-card:hover {
          transform: translateY(-4px);
        }

        @media (max-width: 768px) {
          .technical-showcase {
            padding: 1rem;
          }
          
          .tech-stack-grid {
            grid-template-columns: 1fr;
            gap: 1rem;
          }
          
          .folder-structure {
            font-size: 0.75rem;
          }
          
          .sub-items {
            margin-left: 1rem;
          }
          
          .tech-badge {
            font-size: 0.625rem;
            padding: 0.2rem 0.5rem;
            margin: 0.125rem;
          }
        }
      `}} />
    </div>
  );
};

export default TechnicalShowcaseDashboard;